import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Grid,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Grid2,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Pagination,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import CategoryIcon from "@mui/icons-material/Category";
import api from "../services/authService";

import PageHeader from "../components/common/PageHeader";
import SearchFilters from "../components/common/SearchFilters";
import SearchInput from "../components/common/Filters/SearchInput";
import SingleSelectFilter from "../components/common/Filters/SingleSelectFilter";
import CommonPagination from "../components/common/CommonPagination";
import AddButton from "../components/common/Filters/AddButton";
import SearchButton from "../components/common/Filters/SearchButton";
import InputField from "../components/common/FormFields/InputField";
import SelectField from "../components/common/FormFields/SelectField";
import { categoryValidationSchema } from '../validations/categoryValidation';

// Define interfaces for the API data
interface Category {
  _id: string;
  name: string;
  description: string;
  isActive: boolean;
  createdBy: string;
  objectId: string;
  createdAt: string;
  updatedAt: string;
}

interface CategoryResponse {
  data: Category[];
  meta: {
    page: number;
    perPage: number;
    total: number;
    totalPages: number;
  };
}

interface CategoryRow {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  createdAt: string;
  objectId: string;
}

interface CategoryFormData {
  name: string;
  description: string;
  isActive: boolean;
}

const KnowledgeCategories: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // State for category list and pagination
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(isMobile ? 5 : 10);

  // State for search and filters
  const [searchParams, setSearchParams] = useState({
    keyword: "",
    status: "",
  });

  // State for dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // State for form data
  const [formData, setFormData] = useState<CategoryFormData>({
    name: "",
    description: "",
    isActive: true,
  });

  // State for form validation
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // State for snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  // Fetch categories data
  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      try {
        const response = await api.get("/knowledge-categories", {
          params: {
            page: page + 1,
            perPage: pageSize,
            search: searchParams.keyword || undefined,
            isActive: searchParams.status || undefined,
          },
        });
        const data: CategoryResponse = response.data;

        // Transform data for DataGrid
        const rows = data.data.map((item) => ({
          id: item._id,
          name: item.name,
          description: item.description,
          isActive: item.isActive,
          objectId: item.objectId,
          createdAt: new Date(item.createdAt).toLocaleDateString("th-TH"),
        }));

        setCategories(rows);
        setTotalItems(data.meta.total);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setSnackbar({
          open: true,
          message: "ไม่สามารถดึงข้อมูลหมวดหมู่ได้",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [page, pageSize, searchParams]);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  // Handle search
  const handleSearch = (searchValue?: string) => {
    setSearchParams((prev) => ({
      ...prev,
      keyword: searchValue || "",
    }));
    setPage(0); // Reset to first page when searching
  };

  // Handle filter change
  const handleFilterChange = (filterName: string, value: string) => {
    setSearchParams((prev) => ({
      ...prev,
      [filterName]: value,
    }));
    setPage(0); // Reset to first page when filtering
  };

  // Handle add category
  const handleAddCategory = () => {
    setFormData({
      name: "",
      description: "",
      isActive: true,
    });
    setFormErrors({});
    setIsEditMode(false);
    setFormDialogOpen(true);
  };

  // Handle edit category
  const handleEditCategory = async (objectId: string) => {
    try {
      setLoading(true);
      const response = await api.get(`/knowledge-categories/${objectId}`);
      const category = response.data;

      setFormData({
        name: category.name,
        description: category.description || "",
        isActive: category.isActive,
      });

      setFormErrors({});

      setSelectedCategory({ id: objectId, name: category.name });
      setIsEditMode(true);
      setFormDialogOpen(true);
    } catch (error) {
      console.error("Error fetching category details:", error);
      setSnackbar({
        open: true,
        message: "ไม่สามารถดึงข้อมูลหมวดหมู่ได้",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle delete click
  const handleDeleteClick = (category: { id: string; name: string }) => {
    setSelectedCategory(category);
    setDeleteDialogOpen(true);
  };

  // Handle delete confirm
  const handleDeleteConfirm = async () => {
    if (!selectedCategory) return;

    try {
      await api.delete(`/knowledge-categories/${selectedCategory.id}`);

      // Update the local state to remove the deleted item
      setCategories((prev) =>
        prev.filter((item) => item.objectId !== selectedCategory.id)
      );
      setTotalItems((prev) => prev - 1);

      setDeleteDialogOpen(false);
      setSelectedCategory(null);

      // Show success message
      setSnackbar({
        open: true,
        message: "ลบหมวดหมู่สำเร็จ",
        severity: "success",
      });
    } catch (error) {
      console.error("Error deleting category:", error);

      // Show error message
      setSnackbar({
        open: true,
        message: "เกิดข้อผิดพลาดในการลบหมวดหมู่",
        severity: "error",
      });
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedCategory(null);
  };

  const validateField = async (field: string, value: any) => {
    try {
      await categoryValidationSchema.validateAt(field, { [field]: value });
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    } catch (error: any) {
      setFormErrors(prev => ({ ...prev, [field]: error.message }));
    }
  };

  const handleFormChange = async (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
  ) => {
    const { name, value } = e.target;
    if (name) {
      setFormData(prev => ({
        ...prev,
        [name]: name === 'isActive' ? value === 'true' : value,
      }));
      
      // Validate field
      await validateField(name, value);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Validate all fields
      await categoryValidationSchema.validate(formData, { abortEarly: false });

      setLoading(true);

      if (isEditMode && selectedCategory) {
        // Update existing category
        await api.patch(`/knowledge-categories/${selectedCategory.id}`, formData);

        setSnackbar({
          open: true,
          message: 'แก้ไขหมวดหมู่สำเร็จ',
          severity: 'success',
        });
      } else {
        // Create new category
        await api.post('/knowledge-categories', formData);

        setSnackbar({
          open: true,
          message: 'เพิ่มหมวดหมู่สำเร็จ',
          severity: 'success',
        });
      }

      // Close dialog and refresh data
      setFormDialogOpen(false);

      // Refresh the first page to show the new/updated item
      setPage(0);

      // Fetch categories again to update the list
      const response = await api.get('/knowledge-categories', {
        params: {
          page: 1,
          perPage: pageSize,
          search: searchParams.keyword || undefined,
          isActive: searchParams.status || undefined,
        },
      });

      const data: CategoryResponse = response.data;

      // Transform data for DataGrid
      const rows = data.data.map((item) => ({
        id: item._id,
        name: item.name,
        description: item.description,
        isActive: item.isActive,
        objectId: item.objectId,
        createdAt: new Date(item.createdAt).toLocaleDateString('th-TH'),
      }));

      setCategories(rows);
      setTotalItems(data.meta.total);
    } catch (error: any) {
      if (error.name === 'ValidationError') {
        // Handle validation errors
        const errors: Record<string, string> = {};
        error.inner.forEach((err: any) => {
          errors[err.path] = err.message;
        });
        setFormErrors(errors);
      } else {
        console.error('Error saving category:', error);
        setSnackbar({
          open: true,
          message: isEditMode
            ? 'เกิดข้อผิดพลาดในการแก้ไขหมวดหมู่'
            : 'เกิดข้อผิดพลาดในการเพิ่มหมวดหมู่',
          severity: 'error',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle form cancel
  const handleFormCancel = () => {
    setFormDialogOpen(false);
  };

  // Handle close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  // Define columns for DataGrid
  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "ลำดับ",
      width: 80,
      align: "center",
      headerAlign: "center",
      minWidth: 70,
      renderCell: (params) => {
        // Calculate the row number based on current page
        const rowIndex = categories.findIndex((row) => row.id === params.id);
        return rowIndex !== -1 ? page * pageSize + rowIndex + 1 : "";
      },
    },
    {
      field: "name",
      headerName: "ชื่อหมวดหมู่",
      width: isMobile ? 150 : 200,
      flex: 1,
      minWidth: 150,
    },
    {
      field: "description",
      headerName: "คำอธิบาย",
      width: isMobile ? 200 : 300,
      flex: 2,
      minWidth: 200,
      hideable: true,
      headerClassName: isMobile ? "hidden-column" : "",
    },
    {
      field: "isActive",
      headerName: "สถานะ",
      width: isMobile ? 100 : 120,
      align: "center",
      headerAlign: "center",
      minWidth: 100,
      renderCell: (params) => (
        <Box
          sx={{
            color: params.value ? "success.main" : "error.main",
            fontWeight: 500,
            fontSize: isMobile ? "0.875rem" : "1rem",
          }}
        >
          {params.value ? "เปิดใช้งาน" : "ปิดใช้งาน"}
        </Box>
      ),
    },
    {
      field: "createdAt",
      headerName: "วันที่สร้าง",
      width: isMobile ? 120 : 150,
      align: "center",
      headerAlign: "center",
      minWidth: 120,
      hideable: true,
      headerClassName: isTablet ? "hidden-column" : "",
    },
    {
      field: "actions",
      headerName: "จัดการ",
      width: isMobile ? 120 : 150,
      align: "center",
      headerAlign: "center",
      minWidth: 120,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <EditIcon
            onClick={() => handleEditCategory(params.row.objectId)}
            sx={{
              color: "info.main",
              cursor: "pointer",
              fontSize: isMobile ? "1.2rem" : "1.5rem",
              "&:hover": { color: "info.dark" },
            }}
          />
          <DeleteIcon
            onClick={() =>
              handleDeleteClick({
                id: params.row.objectId,
                name: params.row.name,
              })
            }
            sx={{
              color: "error.main",
              cursor: "pointer",
              fontSize: isMobile ? "1.2rem" : "1.5rem",
              "&:hover": { color: "error.dark" },
            }}
          />
        </Box>
      ),
    },
  ];

  // Generate search filters
  const searchFilters = [
    {
      label: "สถานะ",
      value: "status",
      options: [
        { value: "", label: "ทั้งหมด" },
        { value: "true", label: "เปิดใช้งาน" },
        { value: "false", label: "ปิดใช้งาน" },
      ],
      onChange: (value: string) => handleFilterChange("status", value),
    },
  ];

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        maxWidth: "100%",
      }}
    >
      <PageHeader title="หมวดหมู่บทเรียน" />

      <Grid2
        container
        spacing={2}
        sx={{
          alignItems: "end",
          justifyContent: "start",
          mb: { xs: 2, sm: 2, lg: 4 },
        }}
      >
        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <SearchInput
            placeholder="ค้นหาหมวดหมู่"
            onChange={handleSearch}
            value={searchParams.keyword}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <SingleSelectFilter
            filter={searchFilters[0]}
            value={searchParams.status}
            onChange={(event, filter) =>
              handleFilterChange(filter.value, event.target.value)
            }
          />
        </Grid2>
        <Grid2>
          <SearchButton onClick={() => handleSearch(searchParams.keyword)} />
        </Grid2>
        <Grid2 sx={{ marginLeft: { xs: 0, md: 0, lg: "auto" } }}>
          <AddButton label="เพิ่มหมวดหมู่" onClick={handleAddCategory} />
        </Grid2>
      </Grid2>

      <Paper sx={{ width: "100%", overflow: "hidden"}}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ลำดับ</TableCell>
              <TableCell>ชื่อหมวดหมู่</TableCell>
              <TableCell>สถานะ</TableCell>
              <TableCell>จัดการ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.map((category, index) => (
              <TableRow key={category.id}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{category.name}</TableCell>
                <TableCell>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 500,
                      color: category.isActive ? "success.main" : "error.main",
                    }}
                  >
                    {category.isActive ? "เปิดการใช้งาน" : "ปิดการใช้งาน"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => handleEditCategory(category.objectId)}
                  >
                    <Box
                      component="img"
                      src="/images/icons/button-modify.png"
                      alt="View"
                      sx={{ width: 32, height: 32 }}
                    />
                  </IconButton>
                  <IconButton
                    onClick={() =>
                      handleDeleteClick({
                        id: category.objectId,
                        name: category.name,
                      })
                    }
                  >
                    <Box
                      component="img"
                      src="/images/icons/button-bin.png"
                      alt="View"
                      sx={{ width: 32, height: 32 }}
                    />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </TableContainer>
      </Paper>

      <CommonPagination
        count={Math.ceil(totalItems / pageSize)}
        page={page + 1}
        onChange={(event, value) => handlePageChange(value)}
        onRowsPerPageChange={(value) => handlePageSizeChange(value)}
        totalItems={totalItems}
      />

      {/* Dialog for confirming category deletion */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          ท่านต้องการยืนยันการลบหมวดหมู่ ใช่หรือไม่ ?
          <IconButton
            aria-label="close"
            onClick={handleDeleteCancel}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Typography variant="body1">
            คุณต้องการลบหมวดหมู่ "{selectedCategory?.name}" ใช่หรือไม่?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            หมายเหตุ: การลบหมวดหมู่อาจส่งผลกระทบต่อบทเรียนที่อยู่ในหมวดหมู่นี้
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            ยกเลิก
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="primary"
            variant="contained"
          >
            ยืนยัน
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for adding/editing category */}
      <Dialog
        open={formDialogOpen}
        onClose={handleFormCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isEditMode ? 'แก้ไขหมวดหมู่' : 'เพิ่มหมวดหมู่'}
          <IconButton
            aria-label="close"
            onClick={handleFormCancel}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box component="form" onSubmit={handleFormSubmit} sx={{ mt: 2 }}>
            <Grid2 container spacing={4}>
              <Grid2 size={12}>
                <InputField
                  label="ชื่อหมวดหมู่"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleFormChange}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                />
              </Grid2>
              <Grid2 size={12}>
                <InputField
                  label="คำอธิบาย"
                  name="description"
                  multiline
                  rows={4}
                  value={formData.description}
                  onChange={handleFormChange}
                  error={!!formErrors.description}
                  helperText={formErrors.description}
                />
              </Grid2>
              <Grid2 size={12}>
                <SelectField
                  label="สถานะ"
                  name="isActive"
                  value={formData.isActive ? 'true' : 'false'}
                  onChange={handleFormChange}
                  options={[
                    { value: 'true', label: 'เปิดใช้งาน' },
                    { value: 'false', label: 'ปิดใช้งาน' },
                  ]}
                  error={!!formErrors.isActive}
                  helperText={formErrors.isActive}
                />
              </Grid2>
            </Grid2>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFormCancel} color="primary">
            ยกเลิก
          </Button>
          <Button
            onClick={handleFormSubmit}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'กำลังบันทึก...' : 'บันทึก'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default KnowledgeCategories;
